import { toast } from "@/components/common/Toast/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Check, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { useCreateStatement } from "../../../../../api/declarationsApi"
import type { CandidateArticle, ReaderHint } from "../../../../../api/types"
import type { Verdict } from "../../studio/studioTypes"
import { verdictLabelKey } from "../mapping/verdictDisplay"
import type { StatementCard } from "../useStudioV2State"
import { CountPill, headVerb } from "./HeaderSentence"
import { ReaderHintNotes } from "./ReaderHintNotes"
import { VerdictSelect } from "./VerdictSelect"

/**
 * The pool articles no live statement of this Verordnung covers yet: a
 * `scopeAll` statement covers everything, a subset statement exactly its
 * own `articleNumbers` (plus whatever M2 resolved it to). Excluded
 * (rejected) statements cover nothing any more, exactly as everywhere else
 * in this pane. Pool order is kept.
 */
export function uncoveredArticleNumbers(
  cards: readonly StatementCard[],
  pool: readonly CandidateArticle[],
): string[] {
  const live = cards.filter((c) => !c.excluded)
  if (live.some((c) => c.scopeAll)) return []
  const covered = new Set(
    live.flatMap((c) => [...c.articleNumbers, ...c.resolvedArticles]),
  )
  return pool.map((a) => a.articleNumber).filter((n) => !covered.has(n))
}

export interface RemainderCardProps {
  declId: string
  framework: string
  /** The active Verordnung's cards, excluded ones included (they don't count). */
  cards: StatementCard[]
  pool: CandidateArticle[]
  /** Same landing callback the add-statement dialog uses: the mask jumps to
   *  the freshly created card. */
  onStatementCreated?: (framework: string, statementId: string) => void
  /** Opens the add-statement dialog (`AddStatementControl`): the quote-based
   *  path stays one click away in every state of this card. */
  onAddFromQuote: () => void
  /** The count pill opens the article browser over the uncovered articles,
   *  to capture only a part of them as the next statement. Omitted: the
   *  pill stays a plain label. */
  onOpenArticleBrowser?: () => void
  /** The active Verordnung's inquiry template reader hints, keyed by article
   *  number (`useStudioV2State`'s `readerHintsByFramework`). The card notes
   *  every hint of the articles it still holds: an article without a
   *  statement has nowhere else to show it. A hint leaves with its article
   *  once a statement covers it. Optional, defaults empty. */
  readerHintsByArticle?: Map<string, ReaderHint[]>
  /** Lands a reader hint's sheet row (1-based) in the workbook pane
   *  (`StudioV2Layout`'s own `handleJumpToSheetRow`). Omitted (a PDF): the
   *  hint lines stay plain notes. */
  onJumpToSheetRow?: (sheet: string, row: number) => void
}

/**
 * The ghost card after the last statement (review-mask prototype port): the
 * articles no statement of this Verordnung covers. Same header grammar as a
 * real card ("26 von 330 Artikeln sind ohne Aussage [Status wählen]"), but
 * dashed and quiet, and the verdict select IS the action: choosing a status
 * creates the last statement with exactly these articles in one click (a
 * blanket `scopeAll` statement when nothing is covered yet, so it fans out
 * with the pool). The count pill opens the article browser to take only a
 * part of them. Once every article is covered the card says so and keeps
 * only the quote-based "Aussage aus Textstelle" path.
 */
export function RemainderCard({
  declId,
  framework,
  cards,
  pool,
  onStatementCreated,
  onAddFromQuote,
  onOpenArticleBrowser,
  readerHintsByArticle,
  onJumpToSheetRow,
}: RemainderCardProps) {
  const { translate } = useTranslation()
  const createStatement = useCreateStatement()
  const [busy, setBusy] = useState(false)
  const rest = useMemo(
    () => uncoveredArticleNumbers(cards, pool),
    [cards, pool],
  )
  const total = pool.length

  const create = async (verdict: Verdict) => {
    if (busy || rest.length === 0) return
    setBusy(true)
    const blanket = rest.length === total
    try {
      const created = await createStatement.mutateAsync({
        declarationId: declId,
        framework,
        verdict,
        scopeAll: blanket,
        articleNumbers: blanket ? [] : rest,
        statementText: null,
        evidence: null,
      })
      toast.success(
        translate("MC_DECL_V2_REST_CREATED", {
          n: rest.length,
          status: translate(verdictLabelKey(verdict)),
        }),
      )
      onStatementCreated?.(created.framework, created.statementId)
    } catch {
      // `useCreateStatement`'s own onError already toasted the reason.
    } finally {
      setBusy(false)
    }
  }

  const addFromQuote = (
    <button
      type="button"
      className="sv2-hact sv2-hact-ghost"
      title={translate("MC_DECL_V2_REST_ADD_FROM_QUOTE_TITLE")}
      onClick={onAddFromQuote}
    >
      <Plus className="sv2-ic" />
      <span className="sv2-hlong">
        {translate("MC_DECL_V2_REST_ADD_FROM_QUOTE")}
      </span>
      <span className="sv2-hshort">
        {translate("MC_DECL_V2_ADD_STATEMENT_BUTTON")}
      </span>
    </button>
  )

  if (rest.length === 0) {
    return (
      <div
        className="sv2-exrow sv2-unit sv2-rest sv2-rest-done"
        data-testid="remainder-card"
        data-rest="0"
      >
        <div className="sv2-exr1 sv2-hsent">
          {total > 0 && (
            <>
              <Check size={13} className="sv2-restok" aria-hidden="true" />
              <span className="sv2-hw">
                {translate("MC_DECL_V2_REST_DONE", { n: total })}
              </span>
            </>
          )}
          <span className="sv2-hright ml-auto">{addFromQuote}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className="sv2-exrow sv2-unit sv2-rest"
      data-testid="remainder-card"
      data-rest={rest.length}
    >
      <div className="sv2-exr1 sv2-hsent">
        <CountPill
          count={rest.length}
          total={total}
          quiet
          title={
            onOpenArticleBrowser
              ? translate("MC_DECL_V2_REST_PICK_PART")
              : undefined
          }
          onClick={onOpenArticleBrowser}
          translate={translate}
        />
        <span className="sv2-hw sv2-hverb">
          {headVerb(rest.length, translate)}{" "}
          {translate("MC_DECL_V2_HEAD_VERDICT_PENDING")}
        </span>
        {/* Keyed on the remainder: a pick resets the select once the created
            statement has swallowed these articles and the count moved on. */}
        <VerdictSelect
          key={rest.length}
          value={null}
          onChange={(v) => void create(v)}
          disabled={busy}
          triggerLabel={translate("MC_DECL_V2_REST_PICK_STATUS")}
        />
        <span className="sv2-hright ml-auto">{addFromQuote}</span>
      </div>
      {/* The pool is the supplier's, not the declaration's: creating the
          statement adds exactly these articles to this declaration. */}
      <p className="sv2-hint mt-1 px-1" data-testid="remainder-note">
        {translate("MC_DECL_V2_REST_NOTE", { n: rest.length })}
      </p>
      <ReaderHintNotes
        byArticle={readerHintsByArticle}
        articles={rest}
        wrapClassName="sv2-uslot"
        onJumpToRow={onJumpToSheetRow}
      />
    </div>
  )
}
